import { useEffect, useRef, useState } from "react";
import jsQR from "jsqr";
import { Nfc, QrCode, X } from "lucide-react";

export const hasNfc = () => typeof window !== "undefined" && "NDEFReader" in window;

// Lecture continue d'un QR code via la caméra arrière
function QrReader({ onCode, onClose }) {
  const video = useRef(null);
  const cb = useRef(onCode);
  cb.current = onCode;
  const [err, setErr] = useState("");
  useEffect(() => {
    let stream, raf, done = false;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    const tick = () => {
      const v = video.current;
      if (done || !v) return;
      if (v.readyState === v.HAVE_ENOUGH_DATA) {
        canvas.width = v.videoWidth; canvas.height = v.videoHeight;
        ctx.drawImage(v, 0, 0);
        const r = jsQR(ctx.getImageData(0, 0, canvas.width, canvas.height).data, canvas.width, canvas.height);
        if (r?.data) { done = true; navigator.vibrate?.(80); cb.current(r.data); return; }
      }
      raf = requestAnimationFrame(tick);
    };
    navigator.mediaDevices?.getUserMedia({ video: { facingMode: "environment" } })
      .then((s) => { stream = s; if (done) { s.getTracks().forEach((t) => t.stop()); return; } video.current.srcObject = s; video.current.play().catch(() => {}); tick(); })
      .catch(() => setErr("Caméra inaccessible : autorisez l'accès à la caméra."));
    return () => { done = true; cancelAnimationFrame(raf); stream?.getTracks().forEach((t) => t.stop()); };
  }, []);
  return (
    <div className="fixed inset-0 z-[60] bg-black flex flex-col" data-testid="qr-reader">
      <div className="flex items-center justify-between p-4 text-white">
        <span className="font-bold">Visez le QR code du point</span>
        <button data-testid="qr-reader-close" onClick={onClose} className="p-2 rounded-lg bg-white/15"><X className="h-5 w-5" /></button>
      </div>
      {err ? <div className="m-4 text-rose-200 text-sm" data-testid="qr-reader-error">{err}</div>
        : <video ref={video} playsInline muted className="flex-1 object-cover" />}
    </div>
  );
}

// Boutons de lecture : badge NFC (Android) et QR code (tous téléphones)
export function CodeScanner({ onScan, disabled }) {
  const [qr, setQr] = useState(false);
  const [nfcOn, setNfcOn] = useState(false);
  const [err, setErr] = useState("");
  const ctrl = useRef(null);
  useEffect(() => () => ctrl.current?.abort(), []);
  const startNfc = async () => {
    setErr("");
    try {
      ctrl.current?.abort();
      ctrl.current = new AbortController();
      const r = new window.NDEFReader();
      await r.scan({ signal: ctrl.current.signal });
      setNfcOn(true);
      r.onreading = (e) => { navigator.vibrate?.(80); ctrl.current.abort(); setNfcOn(false); onScan(e.serialNumber, "nfc"); };
      r.onreadingerror = () => setErr("Badge illisible, réessayez.");
    } catch { setNfcOn(false); setErr("Lecture NFC impossible : activez le NFC du téléphone."); }
  };
  return (
    <div className="space-y-2">
      <div className={`grid gap-2 ${hasNfc() ? "grid-cols-2" : "grid-cols-1"}`}>
        {hasNfc() && (
          <button data-testid="scan-nfc-btn" disabled={disabled} onClick={startNfc}
            className={`flex flex-col items-center gap-1 rounded-xl py-4 font-bold text-sm transition-colors disabled:opacity-40 ${nfcOn ? "bg-emerald-600 text-white animate-pulse" : "bg-[#0B2A7A] text-white"}`}>
            <Nfc className="h-6 w-6" />{nfcOn ? "Approchez le badge…" : "Badge NFC"}
          </button>
        )}
        <button data-testid="scan-qr-btn" disabled={disabled} onClick={() => setQr(true)}
          className="flex flex-col items-center gap-1 rounded-xl py-4 font-bold text-sm bg-[#C9962E] text-[#0B1A4A] disabled:opacity-40">
          <QrCode className="h-6 w-6" />QR code
        </button>
      </div>
      {err && <div data-testid="scan-error" className="text-xs text-rose-700">{err}</div>}
      {qr && <QrReader onClose={() => setQr(false)} onCode={(c) => { setQr(false); onScan(c, "qr"); }} />}
    </div>
  );
}
